/**
 * Entitlement Management Dialog
 * Admin controls: change plan, top-up credits, revoke.
 */

import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import api from '../../services/api';
import type { AdminUserEntitlementSummary } from '../../routes/admin/billing';

// Every account is prepaid (F8). The two tiers the admin API accepts:
//   FREE    — unpaid: watermark + 1080p, the free credit allotment.
//   CREDITS — paid (watermark-free + 4K): an exact credit balance.
type Tier = 'FREE' | 'CREDITS';

const TIER_LABEL: Record<Tier, string> = {
  FREE: 'Free (3 credits, watermark)',
  CREDITS: 'Credits (paid · watermark-free + 4K)',
};

function tierOf(user: { source: string }): Tier {
  if (user.source === 'STRIPE' || user.source === 'ADMIN_GRANT') return 'CREDITS';
  return 'FREE';
}

interface Props {
  user: AdminUserEntitlementSummary;
  onClose: () => void;
  onChanged: () => Promise<void>;
}

interface HistoryEntry {
  id: string;
  action: string;
  fromPlan: string | null;
  toPlan: string | null;
  delta: number | null;
  notes: string | null;
  adminId: string | null;
  createdAt: string;
}

export function EntitlementDialog({ user, onClose, onChanged }: Props) {
  const [tier, setTier] = useState<Tier>(tierOf(user));
  const [credits, setCredits] = useState<string>(
    user.videosRemaining != null ? String(user.videosRemaining) : ''
  );
  const [grantExpiresAt, setGrantExpiresAt] = useState<string>(
    user.grantExpiresAt ? user.grantExpiresAt.slice(0, 16) : '' // datetime-local format
  );
  const [notes, setNotes] = useState('');
  const [topUpDelta, setTopUpDelta] = useState('');
  const [topUpReason, setTopUpReason] = useState('');
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadHistory();
  }, [user.userId]);

  async function loadHistory() {
    try {
      const r = await api.get(`/admin/users/${user.userId}/entitlement`);
      setHistory(r.data.history);
    } catch (err) {
      console.error('Failed to load history:', err);
    }
  }

  async function handleGrant() {
    if (tier === 'CREDITS' && credits === '') {
      setError('Enter the number of credits to grant');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const body: Record<string, unknown> = { tier };
      if (credits !== '') body.credits = Number(credits);
      if (grantExpiresAt) body.grantExpiresAt = new Date(grantExpiresAt).toISOString();
      if (notes) body.notes = notes;
      await api.put(`/admin/users/${user.userId}/entitlement`, body);
      await onChanged();
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Grant failed');
    } finally {
      setBusy(false);
    }
  }

  async function handleTopUp() {
    const n = Number(topUpDelta);
    if (!Number.isInteger(n) || n === 0) {
      setError('Top-up must be a non-zero integer');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.post(`/admin/users/${user.userId}/entitlement/top-up`, {
        delta: n,
        reason: topUpReason || undefined,
      });
      await onChanged();
      await loadHistory();
      setTopUpDelta('');
      setTopUpReason('');
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Top-up failed');
    } finally {
      setBusy(false);
    }
  }

  async function handleRevoke() {
    if (!confirm(`Revoke ${user.email}'s plan back to NONE?`)) return;
    setBusy(true);
    setError(null);
    try {
      await api.delete(`/admin/users/${user.userId}/entitlement`, {
        data: { reason: notes || 'revoked by admin' },
      });
      await onChanged();
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Revoke failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Manage entitlement</DialogTitle>
          <DialogDescription>
            {user.displayName ? `${user.displayName} · ` : ''}{user.email}
            <br />
            Current tier: <Badge className="ml-1">{tierOf(user)}</Badge> ({user.source})
          </DialogDescription>
        </DialogHeader>

        {error && (
          <div className="rounded border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
            {error}
          </div>
        )}

        {/* Grant / change plan */}
        <div className="space-y-4 border-b pb-4">
          <h3 className="font-semibold">Set plan</h3>

          <div>
            <Label>Tier</Label>
            <select
              className="w-full mt-1 rounded border bg-background px-3 py-2"
              value={tier}
              onChange={(e) => setTier(e.target.value as Tier)}
            >
              {(['FREE', 'CREDITS'] as const).map((t) => (
                <option key={t} value={t}>{TIER_LABEL[t]}</option>
              ))}
            </select>
          </div>

          <div>
            <Label htmlFor="entitlement-credits">
              Credit balance ({tier === 'FREE' ? 'leave blank for the standard 3' : 'exact number of credits to grant'})
            </Label>
            <Input
              id="entitlement-credits"
              type="number"
              min={0}
              value={credits}
              onChange={(e) => setCredits(e.target.value)}
              placeholder={tier === 'FREE' ? '3' : 'e.g. 10'}
            />
          </div>

          <div>
            <Label>Grant expires at (optional; leave blank for never)</Label>
            <Input
              type="datetime-local"
              value={grantExpiresAt}
              onChange={(e) => setGrantExpiresAt(e.target.value)}
            />
          </div>

          <div>
            <Label>Notes</Label>
            <Input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Why this grant? (e.g., early advisor, beta tester)"
            />
          </div>

          <div className="flex gap-2">
            <Button onClick={handleGrant} disabled={busy}>
              Apply plan change
            </Button>
            <Button variant="destructive" onClick={handleRevoke} disabled={busy}>
              Revoke to NONE
            </Button>
          </div>
        </div>

        {/* Top-up credits */}
        <div className="space-y-3 border-b pb-4">
          <h3 className="font-semibold">Top-up credits</h3>
          <p className="text-sm text-muted-foreground">
            Adds (or subtracts if negative) to this user's remaining video credits without changing
            their plan. Useful for compensation or one-off grants.
          </p>
          <div className="flex gap-2 items-end">
            <div className="flex-1">
              <Label>Delta</Label>
              <Input
                type="number"
                value={topUpDelta}
                onChange={(e) => setTopUpDelta(e.target.value)}
                placeholder="+3, -2, etc."
              />
            </div>
            <div className="flex-1">
              <Label>Reason</Label>
              <Input
                value={topUpReason}
                onChange={(e) => setTopUpReason(e.target.value)}
                placeholder="e.g., refund, goodwill"
              />
            </div>
            <Button onClick={handleTopUp} disabled={busy}>Top up</Button>
          </div>
        </div>

        {/* History */}
        <div>
          <h3 className="font-semibold mb-2">History</h3>
          <div className="max-h-48 overflow-y-auto text-sm border rounded">
            {history.length === 0 ? (
              <div className="p-3 text-muted-foreground">No changes yet.</div>
            ) : (
              <ul className="divide-y">
                {history.map((h) => (
                  <li key={h.id} className="p-2">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">{h.action}</Badge>
                      {h.fromPlan && h.toPlan && h.fromPlan !== h.toPlan && (
                        <span className="text-xs text-muted-foreground">
                          {h.fromPlan} → {h.toPlan}
                        </span>
                      )}
                      {h.delta != null && (
                        <span className="text-xs">Δ {h.delta > 0 ? `+${h.delta}` : h.delta}</span>
                      )}
                      <span className="ml-auto text-xs text-muted-foreground">
                        {new Date(h.createdAt).toLocaleString()}
                      </span>
                    </div>
                    {h.notes && <div className="text-xs text-muted-foreground mt-1">{h.notes}</div>}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
